import { useState } from "react";
import { toast } from "sonner";
import { LogIn, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Seo } from "@/components/site/Seo";
import { adminLogin } from "@/lib/api";
import { devLog } from "@/lib/devLog";

// Titles differ based on which admin surface the login card is protecting.
// A lookup map avoids nested ternaries and makes future titles trivial to add.
const TITLE_COPY = {
  leads: "Sign in to view leads",
  blog: "Sign in to manage blog posts",
  default: "Sign in to the admin console",
};

/**
 * Email + password admin sign-in. On success the backend sets an HttpOnly
 * session cookie — nothing is stored client-side. The parent component is
 * notified via `onAuthenticated` so it can refresh data.
 */
export const AdminLoginCard = ({ onAuthenticated, titleKey = "default" }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const titleCopy = TITLE_COPY[titleKey] || TITLE_COPY.default;

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!email.trim() || !password) {
      toast.error("Enter your admin email and password.");
      return;
    }
    setLoading(true);
    try {
      await adminLogin(email.trim(), password);
      toast.success("Signed in.");
      onAuthenticated?.();
    } catch (err) {
      devLog.warn("AdminLogin: sign-in failed", err);
      const status = err?.response?.status;
      toast.error(status === 401 ? "Invalid email or password." : "Sign-in failed. Please retry.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="admin-login-page" className="mx-auto w-full max-w-md px-4 py-20 sm:px-6">
      <Seo title="Admin · Sign In" path="/admin-clan" noindex />
      <div className="rounded-2xl border border-slate-200 bg-white p-8">
        <div className="flex items-center gap-2 text-emerald-700">
          <ShieldCheck className="h-5 w-5" />
          <span className="text-sm font-semibold">Admin Console</span>
        </div>
        <h1 className="mt-2 text-2xl font-extrabold text-slate-900">{titleCopy}</h1>
        <p className="mt-2 text-sm text-slate-600">
          Enter your admin email and password to manage blog posts and review professional inquiries.
        </p>
        <form onSubmit={handleSubmit} className="mt-6 space-y-3" data-testid="admin-login-form">
          <div>
            <Label htmlFor="admin-email" className="text-xs font-semibold text-slate-700">
              Email
            </Label>
            <Input
              id="admin-email"
              data-testid="admin-email-input"
              type="email"
              autoComplete="username"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="mt-1 h-11 rounded-lg border-slate-200"
              placeholder="you@dannyclans.com"
            />
          </div>
          <div>
            <Label htmlFor="admin-password" className="text-xs font-semibold text-slate-700">
              Password
            </Label>
            <Input
              id="admin-password"
              data-testid="admin-password-input"
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="mt-1 h-11 rounded-lg border-slate-200"
              placeholder="••••••••"
            />
          </div>
          <Button
            type="submit"
            data-testid="admin-login-button"
            disabled={loading}
            className="h-11 w-full rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-70"
          >
            <LogIn className="mr-1.5 h-4 w-4" /> {loading ? "Signing in…" : "Sign in"}
          </Button>
        </form>
      </div>
    </div>
  );
};
